import { api, apiClient } from '@/lib/api/client';
import { useAuthStore } from '@/lib/auth/store';
import { USER_ROLES } from '@/lib/constants/roles';
import { ensureArray } from '@/lib/utils/ensure-array';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { toast } from 'sonner';
import type {
  AdminHistoricalCampaignRequest,
  CampaignActionRequest,
  CampaignRow,
  ContinuousBaseUploadLogFilters,
  ContinuousScheduleRow,
  ContinuousSummaryLogAction,
  CvcLogDetails,
  CvcLogRow,
  HistoricalCampaignRequest,
  ReportDownloadKind,
} from './types';
import { useCallback } from 'react';
import type { BaseFileMode } from '../voice-campaigns/types';

const POLL_MS = 30_000;

export const campaignKeys = {
  all: ['campaigns'] as const,
  live: (userId: string | number | null) => ['campaigns','live', userId] as const,
  schedule: (userId: string | number | null) => ['campaigns', 'schedule', userId] as const,
  logs: (
    userId: string | number | null,
    campaignId: number | string | null,
    filters?: { startDate?: string; endDate?: string; status?: string },
  ) => ['campaigns', 'continuous-base-upload-logs', campaignId, userId, filters ?? {}] as const,
  adminLive: (userId: string | number | null, role: string | null) =>
    ['campaigns', 'admin-live', userId, role] as const,
};

function useInvalidateCampaigns() {
  const qc = useQueryClient();
  return () => qc.invalidateQueries({ queryKey: campaignKeys.all });
}

/* ---------------- USER role lists ---------------- */

/** GET /api/obd/campaign/{userId} — USER live campaign summary. Auto-refresh every 30s. */
export function useLiveCampaigns(options?: { refetchIntervalMs?: number | false }) {
  const userId = useAuthStore((s) => s.userId);
  return useQuery({
    queryKey: campaignKeys.live(userId),
    queryFn: () => api.get<CampaignRow[]>(`/api/obd/campaign/${userId}`),
    enabled: Boolean(userId),
    staleTime: 15_000,
    refetchInterval: options?.refetchIntervalMs ?? POLL_MS,
    select: (data) => ensureArray<CampaignRow>(data),
  });
}

/** GET /api/obd/campaign/schedule/{userId} — USER scheduled (pending) campaigns. */
export function useScheduledCampaigns() {
  const userId = useAuthStore((s) => s.userId);
  return useQuery({
    queryKey: campaignKeys.schedule(userId),
    queryFn: () => api.get<CampaignRow[]>(`/api/obd/campaign/schedule/${userId}`),
    enabled: Boolean(userId),
    staleTime: 30_000,
    select: (data) => ensureArray<CampaignRow>(data),
  });
}

/** POST /api/obd/campaign/historical — USER historical, body has date range. */
export function useHistoricalCampaigns() {
  return useMutation({
    mutationFn: (req: HistoricalCampaignRequest) =>
      api
        .post<CampaignRow[]>('/api/obd/campaign/historical', req)
        .then((res) => ensureArray<CampaignRow>(res)),
  });
}

/* ---------------- Admin / Super-admin lists ---------------- */

/**
 * Admin/super-admin "live campaigns across all owned users". Endpoint
 * splits on role:
 *   - ADMIN          → /api/obd/reseller/campaign/list/{userId}
 *   - SADMIN / SUB   → /api/obd/superadmin/campaign/list/{userId}
 */
export function useAdminLiveCampaigns(options?: { refetchIntervalMs?: number | false }) {
  const userId = useAuthStore((s) => s.userId);
  const role = useAuthStore((s) => s.role);
  const isAdmin = role === USER_ROLES.ADMIN;
  const endpoint = isAdmin
    ? `/api/obd/reseller/campaign/list/${userId}`
    : `/api/obd/superadmin/campaign/list/${userId}`;
  return useQuery({
    queryKey: campaignKeys.adminLive(userId, role),
    queryFn: () => api.get<CampaignRow[]>(endpoint),
    enabled: Boolean(userId),
    staleTime: 15_000,
    refetchInterval: options?.refetchIntervalMs ?? POLL_MS,
    select: (data) => ensureArray<CampaignRow>(data),
  });
}

/**
 * Admin/super-admin historical — body shape adds optional `username` filter
 * for super-admin views; reseller endpoint is the ADMIN equivalent.
 */
export function useAdminHistoricalCampaigns() {
  const role = useAuthStore((s) => s.role);
  const isAdmin = role === USER_ROLES.ADMIN;
  const endpoint = isAdmin
    ? '/api/obd/reseller/campaign/historical'
    : '/api/obd/superadmin/campaign/historical';
  return useMutation({
    mutationFn: (req: AdminHistoricalCampaignRequest) =>
      api
        .post<{ response: CampaignRow[] }>(endpoint, req)
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        .then((res) => ensureArray<CampaignRow>((res as any).response ?? res)),
  });
}

/* ---------------- Prompt-voice campaigns (admin) ---------------- */

export interface PromptVoiceFilterRequest {
  userId?: number;
  startDate: string;
  endDate: string;
  /** Field to filter by (e.g. "promptId", "campaignName"). */
  filterBy?: string;
  filterData?: string;
}

/**
 * POST /api/obd/get/prompt/voice/report (or /reseller/get/prompt/voice/report
 * for ADMIN).
 */
export function usePromptVoiceCampaigns() {
  const role = useAuthStore((s) => s.role);
  const isAdmin = role === USER_ROLES.ADMIN;
  const endpoint = isAdmin
    ? '/api/obd/reseller/get/prompt/voice/report'
    : '/api/obd/get/prompt/voice/report';
  return useMutation({
    mutationFn: (req: PromptVoiceFilterRequest) =>
      api.post<CampaignRow[]>(endpoint, req).then((res) => ensureArray<CampaignRow>(res)),
  });
}

/* ---------------- Row actions (lifecycle) ---------------- */

function useActionMutation(endpoint: string) {
  const invalidate = useInvalidateCampaigns();
  return useMutation({
    mutationFn: (req: CampaignActionRequest) => api.post<{ message?: string }>(endpoint, req),
    onSuccess: () => invalidate(),
  });
}

export const useStopCampaign = () => useActionMutation('/api/obd/campaign/stop');
export const usePauseCampaign = () => useActionMutation('/api/obd/campaign/pause');
export const useResumeCampaign = () => useActionMutation('/api/obd/campaign/resume');

/**
 * Cancel is special — the backend asserts `status == 7 || status == 2`
 * (see CampaignController.canceCompletelVoiceCampaign). Without the status
 * field the request gets rejected with "Parameters Incorrect", which is
 * why we don't simply reuse `useActionMutation` here.
 */
export function useCancelCampaign() {
  const invalidate = useInvalidateCampaigns();
  return useMutation({
    mutationFn: (req: CampaignActionRequest) =>
      api.post<{ message?: string }>('/api/obd/campaign/cancel', {
        campaignId: req.campaignId,
        status: req.status ?? 7,
      }),
    onSuccess: () => invalidate(),
  });
}

/**
 * POST /api/obd/retry/failed/webhook — re-fire every failed webhook delivery
 * for the campaign. Server resets the per-row retry state on the failed_logs
 * table; the next dispatcher tick picks them back up.
 */
export const useRetryFailedWebhook = () => useActionMutation('/api/obd/retry/failed/webhook');

/**
 * POST /api/obd/reset/retry/failed/webhook — sets retry_webhook_count back
 * to 0 on the campaign row, so /retry/failed/webhook becomes available again
 * after it's been throttled out by max-retry caps.
 */
export const useResetRetryFailedWebhook = () =>
  useActionMutation('/api/obd/reset/retry/failed/webhook');

/* ---------------- Campaign extras (retry / status / info) ---------------- */

/**
 * GET /api/obd/campaign/retry/options/:campaignId
 *
 * Backend returns a LIST of retry-option types (legacy DTO
 * `CampaignRetryOptions`: `{ optionName, count }`). Each entry is a call
 * outcome bucket (e.g. "DND", "Failed", "Busy", "NoAnswer") with how many
 * calls landed in it — the UI shows them as checkboxes so the user can
 * pick which outcomes to retry.
 */
export interface CampaignRetryOptionRow {
  optionName: string;
  count: string | number;
}

export function useCampaignRetryOptions(campaignId: number | string | null) {
  return useQuery({
    queryKey: ['campaigns', 'retry-options', campaignId] as const,
    queryFn: () =>
      api
        .get<CampaignRetryOptionRow[]>(`/api/obd/campaign/retry/options/${campaignId}`)
        .then((res) => ensureArray<CampaignRetryOptionRow>(res)),
    enabled: Boolean(campaignId),
    staleTime: 30_000,
  });
}

/**
 * POST /api/obd/campaign/retry — schedule a retry of a completed campaign.
 *
 * Backend (CampaignController.retryVoiceCampaign + DAO) requires
 * `{ campaignId, userId, selectedOptions }` where `selectedOptions` is a
 * colon-joined string of `optionName` values from the retry-options
 * endpoint — or the literal `"All"` when every option is picked.
 */
export interface RetryCampaignRequest {
  campaignId: number;
  userId: number;
  /** Colon-joined list of `optionName` strings, or `"All"`. */
  selectedOptions: string;
}

export function useRetryCampaign() {
  const invalidate = useInvalidateCampaigns();
  return useMutation({
    mutationFn: (req: RetryCampaignRequest) =>
      api.post<{ message?: string }>('/api/obd/campaign/retry', req),
    onSuccess: () => invalidate(),
  });
}

/**
 * POST /api/obd/campaign/status/change — force a campaign into a status
 * different from its current one. SADMIN-only escape hatch.
 */
export interface CampaignStatusChangeRequest {
  campaignId: number;
  /** Numeric status code as used by /campaign/info (e.g. 1=Running, 4=Complete). */
  newStatus: number;
}

export function useChangeCampaignStatus() {
  const invalidate = useInvalidateCampaigns();
  return useMutation({
    mutationFn: (req: CampaignStatusChangeRequest) =>
      api.post<{ message?: string }>('/api/obd/campaign/status/change', req),
    onSuccess: () => invalidate(),
  });
}

/**
 * POST /api/obd/get/voice/base — SADMIN-only.
 *
 * Returns an array with `{ baseUrl, ... }` entries describing the
 * uploaded base file. Used by the "View Base" eye-button on the
 * SADMIN campaign list.
 */
export interface VoiceBaseInfo {
  baseUrl?: string;
  baseName?: string;
}

export function useFetchVoiceBase() {
  return useMutation({
    mutationFn: (campaignId: number) =>
      api.post<VoiceBaseInfo[]>('/api/obd/get/voice/base', {
        startDate: '',
        endDate: '',
        campaignIds: String(campaignId),
      }),
  });
}

/** GET /api/obd/campaign/summary/:campaignId — denser summary card source. */
export function useCampaignSummary(campaignId: number | string | null) {
  return useQuery({
    queryKey: ['campaigns', 'summary', campaignId] as const,
    queryFn: () => api.get<Record<string, unknown>>(`/api/obd/campaign/summary/${campaignId}`),
    enabled: Boolean(campaignId),
    staleTime: 30_000,
  });
}

/** GET /api/obd/campaign/report/:campaignId — alt report fetch (vs /report/generate). */
export function useCampaignReport(campaignId: number | string | null) {
  return useQuery({
    queryKey: ['campaigns', 'report', campaignId] as const,
    queryFn: () => api.get<Record<string, unknown>>(`/api/obd/campaign/report/${campaignId}`),
    enabled: Boolean(campaignId),
    staleTime: 30_000,
  });
}

/** GET /api/obd/inbound/campaignInfo/:campaignId — inbound campaign detail. */
export function useInboundCampaignInfo(campaignId: number | string | null) {
  return useQuery({
    queryKey: ['campaigns', 'inbound-info', campaignId] as const,
    queryFn: () => api.get<Record<string, unknown>>(`/api/obd/inbound/campaignInfo/${campaignId}`),
    enabled: Boolean(campaignId),
    staleTime: 30_000,
  });
}

/** POST /api/obd/campaign/location/list — locations available for a campaign. */
export interface CampaignLocationListRequest {
  campaignId: number;
}
export function useCampaignLocationList() {
  return useMutation({
    mutationFn: (req: CampaignLocationListRequest) =>
      api.post<Array<{ locationId: number; locationName: string }>>(
        '/api/obd/campaign/location/list',
        req,
      ),
  });
}

/* ---------------- Report actions (used in row dropdowns) ---------------- */

export interface GenerateReportRequest {
  campaignId: number;
  reportType:
    | 'full'
    | 'dtmf'
    | 'dtmf1'
    | 'dtmf2'
    | 'connected_calls'
    | 'sms'
    | 'webhook'
    | 'bulk'
    | 'dtmf_bulk'
    | 'connected_bulk';
  campaignIds?: string;
  userId?: number;
  isImpersonated?: boolean;
}

/** POST /api/obd/report/generate */
export function useGenerateCampaignReport() {
  return useMutation({
    mutationFn: (req: GenerateReportRequest) => {
      const isImpersonated = (() => {
        try {
          const raw = sessionStorage.getItem('obd3:auth:stack');
          return raw ? JSON.parse(raw).length > 0 : false;
        } catch {
          return false;
        }
      })();
      return api.post<{ message?: string }>('/api/obd/report/generate', {
        ...req,
        isImpersonated,
      });
    },
  });
}

/** POST /api/obd/webhook/report/generate */
export function useGenerateWebhookReport() {
  return useMutation({
    mutationFn: (req: { campaignId: number }) =>
      api.post<{ message?: string }>('/api/obd/webhook/report/generate', req),
  });
}

/**
 * POST /api/obd/report/pending — returns a string array of un-dialed
 * mobile numbers for the campaign. The legacy UI downloads this as a
 * CSV with one number per line; we mirror that behavior at the caller.
 */
export interface PendingNumbersRequest {
  campaignId: number;
  /** Always 'pending_data' in the legacy app — kept overridable. */
  reportType?: string;
}
export function useDownloadPendingNumbers() {
  return useMutation({
    mutationFn: (req: PendingNumbersRequest) =>
      api.post<string[]>('/api/obd/report/pending', {
        ...req,
        reportType: req.reportType ?? 'pending_data',
      }),
  });
}

/**
 * Shared handler for the inline ⬇ icons in the campaign list cells.
 *
 *   `pending`               → /report/pending → returned numbers downloaded
 *                             client-side as a CSV file.
 *   anything else           → /report/generate with the matching reportType.
 *                             The backend queues a report; the user retrieves
 *                             it from their reports page.
 */
export function useCampaignReportDownload() {
  const { mutate: generateReport, isPending: isGenerating } = useGenerateCampaignReport();
  const { mutate: downloadPending, isPending: isDownloading } = useDownloadPendingNumbers();

  const download = useCallback(
    (row: CampaignRow, kind: ReportDownloadKind) => {
      if (kind === 'pending') {
        return downloadPending(
          { campaignId: row.campaignId },
          {
            onSuccess: (numbers) => {
              if (!numbers || numbers.length === 0) {
                toast.message('No pending numbers for this campaign.');
                return;
              }
              const csv = numbers.map((s) => String(s).substring(0, 10)).join('\n');
              const blob = new Blob([csv], {
                type: 'text/csv;charset=utf-8;',
              });
              const url = URL.createObjectURL(blob);
              const a = document.createElement('a');
              a.href = url;
              a.download = `pending_data_${row.campaignId}.csv`;
              document.body.appendChild(a);
              a.click();
              a.remove();
              URL.revokeObjectURL(url);
              toast.success(`${numbers.length} pending numbers downloaded`);
            },
            onError: () => toast.error('Could not fetch pending numbers.'),
          },
        );
      }
      return generateReport(
        { campaignId: row.campaignId, reportType: kind },
        {
          onSuccess: (res) => toast.success(res?.message ?? 'Report queued'),
          onError: () => toast.error('Could not start the report.'),
        },
      );
    },
    [generateReport, downloadPending],
  );

  return {
    isPending: isGenerating || isDownloading,
    download,
  };
}

/* ---------------- Campaign Operations ---------------- */

/** GET /api/obd/get/running/paused/campaigns/{filter}/{userId} */
export function useRunningPausedCampaigns(filter: 'running' | 'paused') {
  const userId = useAuthStore((s) => s.userId);
  return useQuery({
    queryKey: ['campaigns', 'operations', filter, userId] as const,
    queryFn: () =>
      api.get<CampaignRow[]>(`/api/obd/get/running/paused/campaigns/${filter}/${userId}`),
    enabled: Boolean(userId),
    staleTime: 15_000,
    select: (data) => ensureArray<CampaignRow>(data),
  });
}

export interface BulkCampaignActionRequest {
  /** Comma-joined list of campaign IDs (legacy wire format). */
  campaignIds: string;
  status: number;
  /** "superadmin" for sub/super-admin actions, "user" otherwise. */
  actionOn: 'superadmin' | 'user';
}

/** POST /api/obd/bulk/resume/paused/campaigns — used for bulk pause/resume. */
export function useBulkCampaignAction() {
  const invalidate = useInvalidateCampaigns();
  return useMutation({
    mutationFn: (req: BulkCampaignActionRequest) =>
      api.post<{ message?: string }>('/api/obd/bulk/resume/paused/campaigns', req),
    onSuccess: () => invalidate(),
  });
}

/* ---------------- CvC summary ---------------- */

export interface CvcSummaryRequest {
  userId: number;
  startDate: string;
  endDate: string;
}

export interface CvcSummaryRow {
  id?: number | string;
  campaignId?: number;
  campaignName?: string;
  username?: string;
  userId?: number;
  campaignType?: number | string;
  scheduleDate?: string;
  startTime?: string;
  endDate?: string;
  endtime?: string;
  reportDate?: string;
  totalNumbers?: number;
  totalDnd?: number;
  totalDialed?: number;
  totalConnectedCalls?: number;
  totalFailedCalls?: number;
  totalduration?: number | string;
  totalPulseduration?: number | string;
  totalPulses?: number;
  hasWebhook?: number;
}

/** POST /api/obd/continous/voice/campaign/summary (typo preserved on backend). */
export function useCvcSummary() {
  return useMutation({
    mutationFn: (req: CvcSummaryRequest) =>
      api
        .post<CvcSummaryRow[]>('/api/obd/continous/voice/campaign/summary', req)
        .then((res) => ensureArray<CvcSummaryRow>(res)),
  });
}

/* ---------------- Campaign Info (detail view) ---------------- */

export interface CampaignInfoCampaign {
  campaignId: number;
  campaignName: string;
  templateId: number;
  dtmf?: string;
  menuWaitTime?: string | number;
  retryInterval?: number;
  retries?: number;
  gender?: string;
  language?: string;
  repeatMenu?: string;
  userId?: number;
  username?: string;
  scheduleTime?: string;
  scheduledAt?: string;
  /** true when composed with "Send later" (voice_campaigns.send_later). */
  sendLater?: boolean;
  startDate?: string;
  endDate?: string;
  ringDuration?: number;
  ttsflowName?: string;
  continuous: number;
  /* Live counters — already returned by GET /campaign/info/:id */
  numbersUploaded?: number;
  numbersProcessed?: number;
  skippedCount?: number;
  dndCount?: number;
  callsConnected?: number;
  dtmfCount?: number;
  pulses?: number;
  continueWithBase?: number;
}

export interface CampaignPromptRow {
  promptType?: string;
  promptCategory?: string;
  fileName?: string;
  promptName?: string;
  promptId?: number;
  promptDuration?: number;
  promptUrl?: string;
}

export interface CampaignAgentRow {
  groupId?: number;
  groupName?: string;
  agentDtmf?: string;
}

export interface CampaignSmsRow {
  smsEvent?: string;
  smsText?: string;
  payload?: string;
  header?: string;
  smsMessage: string;
  apiId: number;
  callDurationSMS: number;
  requestType: string;
}

export interface CampaignLocationRow {
  location?: string;
  locationName?: string;
}

export interface CampaignCliRow {
  cli: string;
  cliId?: number;
}

export interface CampaignTtsRow {
  seq?: number;
  ttsType?: string;
  value?: string;
  variableType?: string;
}

export interface CampaignIvrInfo {
  ivrId?: number;
  ivrName?: string;
}

export interface CampaignStatusLog {
  id: number;
  campaignId: number;
  userId: number;
  action: string;
  createdAt: string;
}
export interface CampaignInfoResponse {
  campaign: CampaignInfoCampaign;
  campaignPrompts?: CampaignPromptRow[];
  campaignAgents?: CampaignAgentRow[];
  campaignSms?: CampaignSmsRow[];
  locationList?: CampaignLocationRow[];
  cliList?: CampaignCliRow[];
  ttsRows?: CampaignTtsRow[];
  ivrList?: CampaignIvrInfo[];
  scheduleList?: ContinuousScheduleRow
}

/* ================= Continuous base upload (actions 0/1/2) =================
 * The panel backend validates + logs the attempt, then forwards to the
 * separate continuous service. Actions:
 *   0 = upload new file (baseFile + baseName)
 *   1 = reuse existing base (baseId)
 *   2 = pasted contact list (contactList)
 */

export interface ContinuousBaseUploadResponse {
  message?: string;
  statusCode?: number;
  baseId?: string;
  baseName?: string;
  totalNumbers?: number;
  rejectedNumbers?: number;
  jobId?: string;
  jobState?: string;
}

export interface ContinuousBaseUploadLogRow {
  id: number;
  userId: number;
  campaignId: number;
  /** 0 = file upload, 1 = existing baseId, 2 = pasted list */
  action: number;
  baseName: string | null;
  baseId: number | null;
  jobId: string | null;
  /** queued | processing | complete | error */
  status: string;
  requestData: unknown;
  response: {
    message?: string;
    totalNumbers?: number;
    rejectedNumbers?: number;
    [key: string]: unknown;
  } | null;
  jobResponse: {
    jobState?: string;
    [key: string]: unknown;
  } | null;
  createdAt: string;
  updatedAt: string;
}

/* ---------------- Campaign Allocation (Manage Campaigns SuperAdmin) ---------------- */

/**
 * Nested allocation map: `{location: {dialerId: {campaignId: {cid, cmpName}}}}`.
 * The legacy GET endpoint returns it untyped — we model the leaf shape and
 * leave the outer keys as plain string-keyed records.
 */
export interface AllocatedCampaignLeaf {
  cid: number;
  cmpName: string;
}
export type AllocationMap = Record<string, Record<string, Record<string, AllocatedCampaignLeaf>>>;

/** GET /api/obd/get/campaign/allocation */
export function useCampaignAllocation() {
  return useQuery({
    queryKey: ['campaigns', 'allocation'] as const,
    queryFn: () => api.get<AllocationMap>('/api/obd/get/campaign/allocation'),
    staleTime: 30_000,
  });
}

/** GET /api/obd/get/running/campaigns/{userId} — flat list of currently running campaigns. */
export function useRunningCampaigns() {
  const userId = useAuthStore((s) => s.userId);
  return useQuery({
    queryKey: ['campaigns', 'running', userId] as const,
    queryFn: () => api.get<CampaignRow[]>(`/api/obd/get/running/campaigns/${userId}`),
    enabled: Boolean(userId),
    staleTime: 30_000,
    select: (data) => ensureArray<CampaignRow>(data),
  });
}

export interface AllocationActionRequest {
  /** Comma-joined campaign ids. */
  campaignIds: string;
  /** Comma-joined campaign names (matched 1:1 with ids). */
  campaignNames: string;
  location: string;
  dialerId: string;
}

function useAllocateInvalidate() {
  const qc = useQueryClient();
  return () => {
    qc.invalidateQueries({ queryKey: ['campaigns', 'allocation'] });
    qc.invalidateQueries({ queryKey: ['campaigns', 'cvc-logs'] });
    qc.invalidateQueries({ queryKey: ['campaigns', 'cvc-log-details'] });
    qc.invalidateQueries({ queryKey: ['campaigns', 'running'] });
  };
}

/** POST /api/obd/campaign/add — bind campaigns to a dialer. */
export function useAddCampaignToDialer() {
  const invalidate = useAllocateInvalidate();
  return useMutation({
    mutationFn: (req: AllocationActionRequest) =>
      api.post<{ message?: string }>('/api/obd/campaign/add', req),
    onSuccess: () => invalidate(),
  });
}

/** POST /api/obd/campaign/remove — unbind campaigns from a dialer. */
export function useRemoveCampaignFromDialer() {
  const invalidate = useAllocateInvalidate();
  return useMutation({
    mutationFn: (req: AllocationActionRequest) =>
      api.post<{ message?: string }>('/api/obd/campaign/remove', req),
    onSuccess: () => invalidate(),
  });
}

/* ---------------- Per-campaign Edit CLI / Edit Location ---------------- */

/** POST /api/obd/campaign/cli/list — current CLIs on a campaign. */
export function useCampaignCliList() {
  return useMutation({
    mutationFn: (campaignId: number) =>
      api
        .post<Array<{ cliId: number; cli: string }>>('/api/obd/campaign/cli/list', { campaignId })
        .then((res) => ensureArray<{ cliId: number; cli: string }>(res)),
  });
}

/** POST /api/obd/edit/campaign/cli/ — replace the CLI list bound to a campaign. */
export function useEditCampaignCli() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (req: { campaignId: number; cliList: Array<{ cliId: number; cli: string }> }) =>
      api.post<{ message?: string }>('/api/obd/edit/campaign/cli/', {
        campaignId: req.campaignId,
        cli: JSON.stringify({ cliList: req.cliList }),
      }),
    onSuccess: () => qc.invalidateQueries({ queryKey: campaignKeys.all }),
  });
}

/** POST /api/obd/edit/campaign/cli/archive — archive specific CLIs that were dropped. */
export function useArchiveCampaignCli() {
  return useMutation({
    mutationFn: (req: { campaignId: number; clis: string[] }) =>
      api.post<{ message?: string }>('/api/obd/edit/campaign/cli/archive', {
        campaignId: req.campaignId,
        clis: req.clis.join(','),
      }),
  });
}

/** POST /api/obd/edit/campaign/location/ — replace the location list bound to a campaign. */
export function useEditCampaignLocation() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (req: { campaignId: number; locationIds: string[] }) =>
      api.post<{ message?: string }>('/api/obd/edit/campaign/location/', {
        campaignId: String(req.campaignId),
        location: JSON.stringify({ locationList: req.locationIds }),
      }),
    onSuccess: () => qc.invalidateQueries({ queryKey: campaignKeys.all }),
  });
}

/** GET /api/obd/campaign/info/{campaignId} */
export function useCampaignInfo(campaignId: string | number | null) {
  return useQuery({
    queryKey: ['campaigns', 'info', campaignId] as const,
    queryFn: () => api.get<CampaignInfoResponse>(`/api/obd/campaign/info/${campaignId}`),
    enabled: Boolean(campaignId),
    staleTime: 30_000,
  });
}

/** GET /api/obd/campaign/status-logs/:campaignId */
export function useCampaignStatusLogs(campaignId: string | number | null, enabled = true) {
  return useQuery({
    queryKey: ['campaigns', 'status-logs', campaignId] as const,
    queryFn: () => api.get<CampaignStatusLog[]>(`/api/obd/campaign/status-logs/${campaignId}`),
    enabled: Boolean(campaignId) && enabled,
    staleTime: 30_000,
    select: (data) => ensureArray<CampaignStatusLog>(data),
  });
}

export function useCvcLogs(opts: {
  startDate: string;
  endDate: string;
  action?: ContinuousSummaryLogAction;
}) {
  return useQuery({
    queryKey: ['campaigns', 'cvc-logs', opts.startDate, opts.endDate, opts.action ?? 'all'],
    queryFn: async () => {
      const data = await api.post<CvcLogRow[]>('api/obd/get/continuous-summary/logs', {
        startDate: opts.startDate,
        endDate: opts.endDate,
        ...(opts.action ? { action: opts.action } : {}),
      });
      return data;
    },
  });
}

export function useCvcLogDetails(id: number | null) {
  return useQuery({
    queryKey: ['campaigns', 'cvc-log-details', id],
    queryFn: async () => {
      const data = await api.post<CvcLogDetails>('api/obd/get/continuous-summary/logs/details', {
        id,
      });
      return data;
    },
    enabled: id !== null,
    staleTime: Infinity, // audit rows are immutable — cache forever
  });
}

/** POST /api/obd/campaign/continuous/base — panel proxies to the continuous service. */
export function useContinuousBaseUpload() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (p: {
      userId: number;
      campaignId: number;
      mode: BaseFileMode;
      baseName?: string;
      baseId?: string;
      contactList?: string;
      file?: File | null;
      apiKeyId: string;
    }) => {
      const campaign: Record<string, string | number> = {
        userId: p.userId,
        campaignId: p.campaignId,
        apiKeyId: p.apiKeyId,
      };
      let action: 0 | 1 | 2;
      if (p.mode === 'newBaseFile') {
        action = 0;
        campaign.action = 0;
        campaign.baseName = p.baseName ?? '';
      } else if (p.mode === 'oldBaseFile') {
        action = 1;
        campaign.action = 1;
        campaign.baseId = p.baseId ?? '';
      } else {
        action = 2;
        campaign.action = 2;
        campaign.contactList = p.contactList ?? '';
      }

      const fd = new FormData();
      fd.append('campaign', JSON.stringify(campaign));
      if (action === 0 && p.file) {
        fd.append('baseFile', p.file, p.file.name);
      }

      const res = await api.post<ContinuousBaseUploadResponse>(
        '/api/obd/campaign/continuous/base',
        fd,
        { headers: { 'Content-Type': 'multipart/form-data' } },
      );
      return res;
    },
    onSuccess: () => qc.invalidateQueries({ queryKey: campaignKeys.all }),
  });
}

/** POST /api/obd/get/continuous-base-upload/logs — server syncs active rows
 *  (processing → complete/error), stores the result, then responds. */
export function useContinuousBaseUploadLogs(
  campaignId: number | null,
  filters: ContinuousBaseUploadLogFilters = {},
) {
  const userId = useAuthStore((s) => s.userId);
  return useQuery({
    queryKey: campaignKeys.logs(userId, campaignId ?? 'none', filters),
    queryFn: () =>
      api.post<ContinuousBaseUploadLogRow[]>('/api/obd/get/continuous-base-upload/logs', {
        userId: Number(useAuthStore.getState().userId),
        campaignId: campaignId ?? undefined,
        startDate: filters.startDate || undefined,
        endDate: filters.endDate || undefined,
        status: filters.status || undefined,
      }),
    enabled: Boolean(campaignId),
    refetchInterval: (query) => {
      const rows = query.state.data;
      const active = rows?.some((r) => r.status === 'queued' || r.status === 'processing');
      return active ? 5000 : false;
    },
  });
}

export interface SkippedNumbersPage {
  total: number;
  /** Rows matching the search (== total when no search). */
  matched: number;
  available: boolean;
  headers: string[];
  rows: string[][];
  page: number;
  size: number;
}

export function useSkippedNumbers(
  campaignId: number | string,
  page: number,
  size: number,
  q: string,
  enabled: boolean,
) {
  return useQuery({
    queryKey: ['campaigns', 'skipped-numbers', campaignId, page, size, q] as const,
    queryFn: () =>
      api.get<SkippedNumbersPage>(`/api/obd/base/skipped/${campaignId}`, {
        params: { page, size, q },
      }),
    enabled: enabled && Boolean(campaignId),
    staleTime: 60_000,
    placeholderData: (prev) => prev,
  });
}

export function useBaseNumbers(
  campaignId: number | string,
  page: number,
  size: number,
  q: string,
  enabled: boolean,
) {
  return useQuery({
    queryKey: ['campaigns', 'base-numbers', campaignId, page, size, q] as const,
    queryFn: () =>
      api.get<SkippedNumbersPage>(`/api/obd/base/file/${campaignId}`, {
        params: { page, size, q },
      }),
    enabled: enabled && Boolean(campaignId),
    staleTime: 60_000,
    placeholderData: (prev) => prev,
  });
}

export async function downloadSkippedNumbers(campaignId: number | string) {
  const res = await apiClient.get<Blob>(
    `/api/obd/base/skipped/${campaignId}/download`,
    { responseType: 'blob' },
  );
  const url = URL.createObjectURL(res.data);
  const a = document.createElement('a');
  a.href = url;
    a.download = `skipped_numbers_campaign_${campaignId}.csv`;
  document.body.appendChild(a);
  a.click();
  a.remove();
  URL.revokeObjectURL(url);
}
